import React, { useState, useEffect, useCallback, useMemo, useRef, Suspense } from 'react';
import { Navbar } from './components/Navbar';
import { Hero } from './components/Hero';
import { TreatmentAreas } from './components/TreatmentAreas';
import { PriceCalculator } from './components/PriceCalculator';
import { DoctorTrustSection } from './components/DoctorTrustSection';
import { TechnologySection } from './components/TechnologySection';
import { BlogSection } from './components/BlogSection';
import { FaqSection } from './components/FaqSection';
import { LocationSection } from './components/LocationSection';
import { StickyMobileBar } from './components/StickyMobileBar';
import { Footer } from './components/Footer';
import { ApollonLanding } from './components/ApollonLanding';
import { BlogPost } from './types';
import { BLOG_POSTS } from './data/blogPosts';
import { lazyWithRetry } from './utils/lazyWithRetry';
import { shouldShowSiteSwitcher } from './utils/siteConfig';

// Lazy-load secondary route pages & heavy interactive overlays for minimal initial JS footprint
const BlogPage = lazyWithRetry(() => import('./components/BlogPage').then((m) => ({ default: m.BlogPage })));
const NotFoundPage = lazyWithRetry(() => import('./components/NotFoundPage').then((m) => ({ default: m.NotFoundPage })));
const LeadModal = lazyWithRetry(() => import('./components/LeadModal').then((m) => ({ default: m.LeadModal })));
const LiveChatWidget = lazyWithRetry(() => import('./components/LiveChatWidget').then((m) => ({ default: m.LiveChatWidget })));
const CookieConsentBanner = lazyWithRetry(() => import('./components/CookieConsentBanner').then((m) => ({ default: m.CookieConsentBanner })));
const NetworkStatusNotifier = lazyWithRetry(() => import('./components/NetworkStatusNotifier').then((m) => ({ default: m.NetworkStatusNotifier })));
const AdminPage = lazyWithRetry(() => import('./components/AdminPage').then((m) => ({ default: m.AdminPage })));

// Helper to extract stored secret admin path from window SSR injection or localStorage
const getStoredSecretPath = (): string => {
  if (typeof window !== 'undefined') {
    if ((window as any).__INITIAL_ADMIN_SECURITY_CONFIG__?.secretPath) {
      return (window as any).__INITIAL_ADMIN_SECURITY_CONFIG__.secretPath;
    }
    try {
      const stored = localStorage.getItem('admin_secret_path');
      if (stored && stored.trim()) return stored.trim();
    } catch {}
  }
  return '/yonetim-girisi';
};

// Normalize string for case, slash, and Turkish character tolerance
const normalizePathString = (val: string): string => {
  if (!val) return '';
  let str = String(val).trim().toLowerCase();
  try {
    str = decodeURIComponent(str);
  } catch {}
  // Strip query strings and hashes if present
  str = str.split('?')[0].split('#')[0];
  // Strip leading and trailing slashes
  str = str.replace(/^\/+|\/+$/g, '');
  return str;
};

const toAsciiPath = (val: string): string => {
  const normalized = normalizePathString(val);
  const trMap: Record<string, string> = {
    'ç': 'c', 'ğ': 'g', 'ı': 'i', 'i': 'i', 'ö': 'o', 'ş': 's', 'ü': 'u'
  };
  return normalized.replace(/[çğıiöşü]/g, (m) => trMap[m] || m);
};

export const isSecretAdminMatch = (
  currentPathname: string,
  currentHash: string,
  configuredSecretPath: string
): boolean => {
  const normPath = normalizePathString(currentPathname || '');
  const asciiPath = toAsciiPath(currentPathname || '');

  const normHash = normalizePathString((currentHash || '').replace(/^#+/, ''));
  const asciiHash = toAsciiPath((currentHash || '').replace(/^#+/, ''));

  const targetSecret = normalizePathString(configuredSecretPath || '/yonetim-girisi');
  const asciiSecret = toAsciiPath(configuredSecretPath || '/yonetim-girisi');

  // 1. Direct match with configured custom secret (e.g. /trinovex-yonetim, /Trinovex-Yonetim, /trinovex-yönetim)
  if (
    normPath === targetSecret ||
    asciiPath === asciiSecret ||
    normPath.startsWith(targetSecret + '/') ||
    asciiPath.startsWith(asciiSecret + '/')
  ) {
    return true;
  }

  // 2. Hash match with configured custom secret (e.g. #trinovex-yonetim, #Trinovex-Yonetim)
  if (
    normHash === targetSecret ||
    asciiHash === asciiSecret ||
    normHash.startsWith(targetSecret + '/') ||
    asciiHash.startsWith(asciiSecret + '/')
  ) {
    return true;
  }

  // 3. Fallback default triggers
  const defaultSecrets = ['yonetim-girisi', 'yonetim_girisi', 'admin-girisi', 'admin_girisi'];
  for (const def of defaultSecrets) {
    if (normPath === def || asciiPath === def || normHash === def || asciiHash === def) {
      return true;
    }
  }

  return false;
};

const getInitialRoute = (secretPath?: string): { view: 'home' | 'blog' | 'admin' | 'not-found'; slug: string } => {
  if (typeof window === 'undefined') return { view: 'home', slug: '' };
  const currentSecret = secretPath || getStoredSecretPath();
  const rawPathname = window.location.pathname || '/';
  const hash = window.location.hash || '';

  // Check for secret admin view trigger (e.g. /Trinovex-Yonetim, /yonetim-girisi, #trinovex-yonetim)
  if (isSecretAdminMatch(rawPathname, hash, currentSecret)) {
    return { view: 'admin', slug: '' };
  }

  const lowerPath = rawPathname.toLowerCase().trim();
  if (lowerPath.startsWith('/blog')) {
    let slug = rawPathname.replace(/^\/blog\/?/i, '').replace(/\/+$/, '').trim();
    try {
      slug = decodeURIComponent(slug);
    } catch {}
    return { view: 'blog', slug };
  }
  if (hash.startsWith('#blog-')) {
    return { view: 'blog', slug: hash.replace('#blog-', '') };
  }
  
  if (lowerPath === '/livechat' || lowerPath === '/livechat/' || lowerPath.startsWith('/livechat/')) {
    if (typeof window !== 'undefined') {
      const isFemaleDomain = window.location.hostname.includes('kadin') || lowerPath.includes('kadin');
      const accessKey = isFemaleDomain
        ? 'MF8wODEzYWM0MmMwY2Q0Zjk2YjgxNDgzMzcxZDVkMDM2Zl9lYzc0ZDcyM2I2MTUmNWMwOTZkMjA4NzIzNDA1ZTBiZTY3NThkNjZiOWVhNTE='
        : 'MF9jNTYxZDdjMTg2YTc0ZjYzYjE5NTMyNzMwOGMxMmZhMF9lYzc0ZDcyM2I2MTUmMWJmN2RiOTVjNjZlODUwN2ZhMGQ2YmM2ODdmMWRmMmU=';
      window.location.replace(`https://trinovex.a.gdms.cloud/liveChat?liveChatAccess=${encodeURIComponent(accessKey)}`);
    }
    return { view: 'home', slug: '' };
  }

  if (lowerPath === '/' || lowerPath === '' || lowerPath === '/index.html') {
    return { view: 'home', slug: '' };
  }

  return { view: 'not-found', slug: rawPathname };
};

import { SiteProvider, useSite } from './context/SiteContext';

function AppContent() {
  const { siteKey, setSiteKey, siteProfile, isApollon, isFemale, isErkek } = useSite();
  const [secretAdminPath, setSecretAdminPath] = useState<string>(getStoredSecretPath);
  const initialRoute = getInitialRoute(secretAdminPath);
  const [posts, setPosts] = useState<BlogPost[]>(() => {
    if (typeof window !== 'undefined') {
      const singlePost = (window as any).__INITIAL_BLOG_POST__;
      const allBlogs = (window as any).__INITIAL_BLOGS__;
      if (Array.isArray(allBlogs) && allBlogs.length > 0) return allBlogs;
      if (singlePost && singlePost.slug) {
        return [singlePost];
      }
    }
    return BLOG_POSTS;
  });

  // Strictly filter blogs based on active siteKey (Erkek on Erkek, Kadın on Kadın, Apollon on Apollon)
  const effectivePosts = useMemo(() => {
    return posts.filter((post) => {
      const ts = (post.target_site || (post as any).targetSite || '').toLowerCase().trim();
      const slug = (post.slug || '').toLowerCase();
      const title = (post.title || '').toLowerCase();
      const category = (post.category || '').toLowerCase();

      const isFemale = slug.includes('kadin') || title.includes('kadın') || title.includes('kadin') || category.includes('kadın') || slug.includes('bikini') || slug.includes('genital-kadin');
      const isMale = slug.includes('erkek') || title.includes('erkek') || category.includes('erkek') || slug.includes('sakal') || slug.includes('sirt') || slug.includes('ense') || slug.includes('boyun') || slug.includes('genital-erkek');

      // Determine site classification
      let effSite = ts;
      if (!effSite || effSite === 'undefined') {
        if (isFemale && isMale) effSite = 'all';
        else if (isFemale) effSite = 'kadin';
        else if (isMale) effSite = 'erkek';
        else effSite = 'apollon';
      }

      if (siteKey === 'kadin') {
        return effSite === 'kadin' || effSite === 'all';
      }
      if (siteKey === 'erkek') {
        return effSite === 'erkek' || effSite === 'all';
      }
      if (siteKey === 'apollon') {
        return effSite === 'apollon';
      }
      return true;
    });
  }, [posts, siteKey]);

  const [isBlogsLoading, setIsBlogsLoading] = useState<boolean>(true);
  const [currentView, setCurrentView] = useState<'home' | 'blog' | 'admin' | 'not-found'>(initialRoute.view);
  const [currentBlogSlug, setCurrentBlogSlug] = useState<string>(initialRoute.slug);
  const [requestedPath, setRequestedPath] = useState<string>(initialRoute.view === 'not-found' ? initialRoute.slug : '');
  
  const [leadModalOpen, setLeadModalOpen] = useState(false);
  const [leadModalTitle, setLeadModalTitle] = useState('Ücretsiz Kıl Muayenesi ve Fiyat Teklifi');
  const [selectedAreasForCalc, setSelectedAreasForCalc] = useState<string[]>([]);

  // Synchronize document title with active site profile
  useEffect(() => {
    if (typeof document !== 'undefined' && currentView === 'home') {
      const siteDisplayName =
        siteProfile?.siteName ||
        siteProfile?.clinicName ||
        (isApollon
          ? 'Apollon Güzellik Merkezi'
          : isFemale
          ? 'Bursa Kadın Lazer Epilasyon'
          : 'Bursa Erkek Lazer Epilasyon');

      if (siteProfile?.metaTitle && !siteProfile.metaTitle.includes('undefined')) {
        document.title = siteProfile.metaTitle;
      } else {
        document.title = `${siteDisplayName} | Nilüfer FSM Bulvarı Doktor Kliniği`;
      }
    }
  }, [siteProfile, currentView, isApollon, isFemale]);

  useEffect(() => {
    // Immediately dismiss preloader on mount
    if (typeof window !== 'undefined' && (window as any).dismissAppPreloader) {
      (window as any).dismissAppPreloader();
    }
  }, []);

  // Fetch security config (Secret Admin Path) and immediately reconcile route
  useEffect(() => {
    fetch('/api/admin-security-config')
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        if (data && data.success && data.secretPath) {
          const freshSecret = data.secretPath;
          setSecretAdminPath(freshSecret);
          try {
            localStorage.setItem('admin_secret_path', freshSecret);
          } catch {}

          // Immediate route matching check on config arrival
          if (typeof window !== 'undefined') {
            const rawPath = window.location.pathname || '/';
            const rawHash = window.location.hash || '';
            if (isSecretAdminMatch(rawPath, rawHash, freshSecret)) {
              setCurrentView('admin');
              setCurrentBlogSlug('');
            }
          }
        }
      })
      .catch((err) => console.warn('Security config fetch error:', err));
  }, []);

  const lastKnownVersionRef = useRef<number | null>(null);

  // Fetch dynamic blogs filtered by target_site with static fallback
  const fetchBlogs = useCallback(async () => {
    setIsBlogsLoading(true);
    try {
      const res = await fetch(`/api/blogs?target_site=${siteKey}&_t=${Date.now()}`, { cache: 'no-store' });
      if (!res.ok) {
        // Fallback gracefully to default blog posts without blocking the user
        return;
      }
      const data = await res.json();
      if (data.success && Array.isArray(data.posts)) {
        setPosts(data.posts);
      }
    } catch (err) {
      console.warn('API blogs fetch failed, using fallback static data:', err);
    } finally {
      setIsBlogsLoading(false);
    }
  }, [siteKey]);

  // Re-fetch blogs whenever siteKey changes
  useEffect(() => {
    fetchBlogs();
  }, [fetchBlogs]);

  // Lightweight check for real-time updates (Zero CPU / Network Overhead)
  const checkContentVersion = useCallback(async () => {
    if (document.visibilityState !== 'visible') return;
    try {
      const res = await fetch(`/api/content-version?_t=${Date.now()}`, { cache: 'no-store' });
      if (res.ok) {
        const data = await res.json();
        if (data.success && typeof data.version === 'number') {
          if (lastKnownVersionRef.current !== null && data.version > lastKnownVersionRef.current) {
            // Content updated on server: Silent refresh without triggering loading spinners
            fetch(`/api/blogs?target_site=${siteKey}&_t=${Date.now()}`, { cache: 'no-store' })
              .then((r) => (r.ok ? r.json() : null))
              .then((d) => {
                if (d && d.success && Array.isArray(d.posts)) {
                  setPosts(d.posts);
                }
              })
              .catch(() => {});
          }
          lastKnownVersionRef.current = data.version;
        }
      }
    } catch (e) {
      // Ignore background check errors
    }
  }, [siteKey]);

  useEffect(() => {
    fetchBlogs().then(() => {
      // Record initial content version
      fetch(`/api/content-version?_t=${Date.now()}`, { cache: 'no-store' })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (d && d.success && typeof d.version === 'number') {
            lastKnownVersionRef.current = d.version;
          }
        })
        .catch(() => {});
    });

    // 1. Check content version every 15 seconds when active
    const timer = setInterval(checkContentVersion, 15000);

    // 2. Check immediately when user switches back to window tab
    const handleFocus = () => {
      checkContentVersion();
    };
    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', handleFocus);
    };
  }, [fetchBlogs, checkContentVersion]);

  // Route Resolver from window.location
  const resolveRoute = useCallback(() => {
    const rawPathname = window.location.pathname || '/';
    const hash = window.location.hash || '';

    // Check for secret admin view trigger
    if (isSecretAdminMatch(rawPathname, hash, secretAdminPath)) {
      setCurrentView('admin');
      setCurrentBlogSlug('');
      setRequestedPath('');
      return;
    }

    const lowerPath = rawPathname.toLowerCase().trim();
    // Check if path is blog page (/blog or /blog/:slug)
    if (lowerPath.startsWith('/blog')) {
      let slug = rawPathname.replace(/^\/blog\/?/i, '').replace(/\/+$/, '').trim();
      try {
        slug = decodeURIComponent(slug);
      } catch {}

      setCurrentView('blog');
      setCurrentBlogSlug(slug);
      setRequestedPath('');

      const currentDomain = siteProfile.domain || (isApollon ? 'apollonguzellik.com.tr' : isFemale ? 'bursakadinlazer.com.tr' : 'bursaerkeklazer.com.tr');

      if (typeof document !== 'undefined') {
        const blogCanonical = slug ? `https://${currentDomain}/blog/${slug}` : `https://${currentDomain}/blog`;
        let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
        if (!canonical) {
          canonical = document.createElement('link');
          canonical.setAttribute('rel', 'canonical');
          document.head.appendChild(canonical);
        }
        canonical.setAttribute('href', blogCanonical);

        document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => {
          el.setAttribute('href', blogCanonical);
        });
      }
    } else if (hash.startsWith('#blog-')) {
      // Legacy hash support -> redirect to /blog/:slug
      const legacySlug = hash.replace('#blog-', '');
      try {
        window.history.replaceState(null, '', `/blog/${legacySlug}`);
      } catch {}
      setCurrentView('blog');
      setCurrentBlogSlug(legacySlug);
      setRequestedPath('');

      const currentDomain = siteProfile.domain || (isApollon ? 'apollonguzellik.com.tr' : isFemale ? 'bursakadinlazer.com.tr' : 'bursaerkeklazer.com.tr');

      if (typeof document !== 'undefined') {
        const blogCanonical = legacySlug ? `https://${currentDomain}/blog/${legacySlug}` : `https://${currentDomain}/blog`;
        let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
        if (!canonical) {
          canonical = document.createElement('link');
          canonical.setAttribute('rel', 'canonical');
          document.head.appendChild(canonical);
        }
        canonical.setAttribute('href', blogCanonical);

        document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => {
          el.setAttribute('href', blogCanonical);
        });
      }
    } else if (lowerPath === '/' || lowerPath === '' || lowerPath === '/index.html') {
      setCurrentView('home');
      setCurrentBlogSlug('');
      setRequestedPath('');
      const currentDomain = siteProfile.domain || (isApollon ? 'apollonguzellik.com.tr' : isFemale ? 'bursakadinlazer.com.tr' : 'bursaerkeklazer.com.tr');
      if (typeof document !== 'undefined') {
        const homeCanonical = `https://${currentDomain}/`;
        let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
        if (!canonical) {
          canonical = document.createElement('link');
          canonical.setAttribute('rel', 'canonical');
          document.head.appendChild(canonical);
        }
        canonical.setAttribute('href', homeCanonical);

        document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => {
          el.setAttribute('href', homeCanonical);
        });
      }
    } else {
      // Unrecognized path -> 404 Not Found
      setCurrentView('not-found');
      setCurrentBlogSlug('');
      setRequestedPath(rawPathname);
      if (typeof document !== 'undefined') {
        const fallbackName = isFemale ? 'Bursa Kadın Lazer' : (isApollon ? 'Apollon Güzellik' : 'Bursa Erkek Lazer');
        document.title = `404 Sayfa Bulunamadı | ${siteProfile.siteName || fallbackName}`;
      }
    }
  }, [secretAdminPath, siteProfile, isApollon, isFemale]);

  // Listen to browser popstate (back/forward) & hashchange
  useEffect(() => {
    resolveRoute();
    window.addEventListener('popstate', resolveRoute);
    window.addEventListener('hashchange', resolveRoute);
    return () => {
      window.removeEventListener('popstate', resolveRoute);
      window.removeEventListener('hashchange', resolveRoute);
    };
  }, [resolveRoute]);

  // Navigation function using History API
  const navigateTo = (url: string) => {
    let target = url;
    if (url === '/admin' || url === 'admin') {
      target = secretAdminPath || '/yonetim-girisi';
    }
    try {
      window.history.pushState(null, '', target);
    } catch {
      // Sandboxed iframes without allow-same-origin may restrict pushState
    }
    resolveRoute();
    try {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {}
  };

  const handleOpenLeadModal = (title?: string) => {
    if (title) setLeadModalTitle(title);
    setLeadModalOpen(true);
  };

  const handleSelectAreaForCalc = (areaName: string) => {
    if (currentView !== 'home') {
      navigateTo('/');
      setTimeout(() => {
        setSelectedAreasForCalc((prev) => (!prev.includes(areaName) ? [...prev, areaName] : prev));
        document.getElementById('fiyat-hesapla')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      setSelectedAreasForCalc((prev) => (!prev.includes(areaName) ? [...prev, areaName] : prev));
      document.getElementById('fiyat-hesapla')?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // If viewing admin route, render full-page admin experience (Login or Dashboard)
  if (currentView === 'admin') {
    return (
      <Suspense fallback={
        <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-6 text-center animate-[fadeInUp_1s_ease-out_forwards]">
          <div className="relative w-20 h-20 flex items-center justify-center mb-8">
            <div className="absolute inset-0 rounded-full border border-amber-500/10 border-t-amber-500/80 animate-[spin_1.5s_cubic-bezier(0.4,0,0.2,1)_infinite]" />
            <div className="absolute inset-2 rounded-full border border-amber-500/10 border-b-amber-500/50 animate-[spin_2s_cubic-bezier(0.4,0,0.2,1)_infinite_reverse]" />
            <div className="text-3xl font-light tracking-tighter bg-gradient-to-br from-amber-200 via-amber-500 to-amber-600 bg-clip-text text-transparent animate-[pulseGlow_2s_ease-in-out_infinite]">
              B
            </div>
          </div>
          <div className="text-center">
            <h2 className="text-[15px] font-semibold text-slate-50 tracking-[0.35em] uppercase mb-2">
              BURSA ERKEK LAZER
            </h2>
            <p className="text-[11px] font-normal text-slate-400 tracking-[0.25em] uppercase">
              Yönetim Paneli
            </p>
          </div>
          <div className="w-[140px] h-px bg-white/5 mt-10 relative overflow-hidden">
            <div className="absolute inset-0 w-full bg-gradient-to-r from-transparent via-amber-500/80 to-transparent animate-[slideLine_2s_ease-in-out_infinite]" />
          </div>
        </div>
      }>
        <AdminPage
          onNavigateHome={() => navigateTo('/')}
          onBlogChanged={fetchBlogs}
          onViewPostInSite={(slug) => navigateTo(`/blog/${slug}`)}
          onPreviewSiteChange={(key) => setSiteKey(key)}
        />
      </Suspense>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 flex flex-col font-sans selection:bg-amber-400 selection:text-slate-950 overflow-x-clip">
      {/* Top Bar & Navigation */}
      <Navbar
        onOpenLeadModal={handleOpenLeadModal}
        onNavigateToBlog={() => navigateTo('/blog')}
        onOpenAdminModal={() => navigateTo('/admin')}
        onNavigateHome={() => navigateTo('/')}
      />

      {/* Main Content: 404 Page vs Single Blog View vs Home Landing */}
      {currentView === 'not-found' ? (
        <Suspense fallback={<div className="min-h-[60vh] flex items-center justify-center"><div className="w-8 h-8 rounded-full border-2 border-amber-500 border-t-transparent animate-spin" /></div>}>
          <NotFoundPage
            requestedPath={requestedPath}
            onNavigateHome={() => navigateTo('/')}
            onNavigateToBlog={() => navigateTo('/blog')}
            onOpenCalculator={() => {
              navigateTo('/');
              setTimeout(() => {
                document.getElementById('fiyat-hesapla')?.scrollIntoView({ behavior: 'smooth' });
              }, 100);
            }}
            onOpenLeadModal={handleOpenLeadModal}
          />
        </Suspense>
      ) : currentView === 'blog' ? (
        <Suspense fallback={<div className="min-h-[60vh] flex items-center justify-center"><div className="w-8 h-8 rounded-full border-2 border-amber-500 border-t-transparent animate-spin" /></div>}>
          <BlogPage
            slug={currentBlogSlug}
            posts={effectivePosts}
            isLoading={isBlogsLoading}
            onNavigateHome={() => navigateTo('/')}
            onNavigateToPost={(slug) => navigateTo(slug ? `/blog/${slug}` : '/blog')}
            onOpenLeadModal={handleOpenLeadModal}
          />
        </Suspense>
      ) : (
        <main className="flex-grow">
          {isApollon ? (
            /* Apollon Güzellik & Medikal Estetik Custom Luxury Landing */
            <ApollonLanding
              onOpenLeadModal={handleOpenLeadModal}
              posts={effectivePosts}
              isBlogsLoading={isBlogsLoading}
              onNavigateToPost={(slug) => navigateTo(`/blog/${slug}`)}
            />
          ) : (
            <>
              {/* 1. Hero Section (Nilüfer FSM Bulvarı & Doctor Clinic Trust & CTAs) */}
              <Hero
                onOpenCalculator={() => {
                  const el = document.getElementById('fiyat-hesapla');
                  if (el) el.scrollIntoView({ behavior: 'smooth' });
                }}
                onOpenLeadModal={handleOpenLeadModal}
              />

              {/* 2. Treatment Areas (Male/Female specific areas & direct WhatsApp quote buttons) */}
              <TreatmentAreas onSelectAreaForCalc={handleSelectAreaForCalc} />

              {/* 3. Interactive Custom Quote & Package Wizard (Saved to MySQL DB + WhatsApp Option) */}
              <PriceCalculator selectedAreasFromParent={selectedAreasForCalc} />

              {/* 4. Doctor Clinic Trust & Medical Grade Standards */}
              <DoctorTrustSection onOpenLeadModal={handleOpenLeadModal} />

              {/* 5. Acısız Soğutmalı Buz Başlık & 3'lü Dalga Boyu Teknolojisi */}
              <TechnologySection />

              {/* 6. SEO Blog & Content Hub */}
              <BlogSection
                posts={effectivePosts}
                isLoading={isBlogsLoading}
                onNavigateToPost={(slug) => navigateTo(`/blog/${slug}`)}
              />

              {/* 7. Sıkça Sorulan Sorular (FAQ) */}
              <FaqSection />

              {/* 8. Konum & Nilüfer FSM Bulvarı Ulaşım */}
              <LocationSection />
            </>
          )}
        </main>
      )}

      {/* Footer */}
      <Footer
        onOpenAdminModal={() => navigateTo('/admin')}
        onNavigateToBlog={() => navigateTo('/blog')}
        onNavigateHome={() => navigateTo('/')}
      />

      {/* Floating Mobile Bar (WhatsApp & Ara) */}
      <StickyMobileBar
        onOpenCalculator={() => {
          if (currentView !== 'home') {
            navigateTo('/');
            setTimeout(() => {
              document.getElementById('fiyat-hesapla')?.scrollIntoView({ behavior: 'smooth' });
            }, 100);
          } else {
            const el = document.getElementById('fiyat-hesapla');
            if (el) el.scrollIntoView({ behavior: 'smooth' });
          }
        }}
      />

      {/* Trinovex Live Chat System */}
      <Suspense fallback={null}>
        <LiveChatWidget />
      </Suspense>

      {/* KVKK & Cookie Consent System (Compliant with Turkish Laws) */}
      <Suspense fallback={null}>
        <CookieConsentBanner />
      </Suspense>

      {/* Real-time Internet Network Disconnect / Restore Status Notifier */}
      <Suspense fallback={null}>
        <NetworkStatusNotifier />
      </Suspense>

      {/* Lead / Quick Consultation Modal */}
      {leadModalOpen && (
        <Suspense fallback={null}>
          <LeadModal
            isOpen={leadModalOpen}
            onClose={() => setLeadModalOpen(false)}
            initialTitle={leadModalTitle}
          />
        </Suspense>
      )}

      {/* Development & Live Preview Mode Switcher (Google AI Studio & Local Dev Only - Hidden on Production Server) */}
      {shouldShowSiteSwitcher() && (
        <aside aria-label="Canlı Tasarım Seçici" className="fixed bottom-20 left-3 sm:left-4 z-40 flex items-center gap-1 sm:gap-1.5 bg-slate-900/95 text-white p-1 sm:px-3 sm:py-1.5 rounded-full shadow-2xl border border-slate-700/80 backdrop-blur-md text-xs select-none">
          <span className="text-slate-400 font-bold text-[10px] sm:text-[11px] hidden md:inline ml-1">Portal:</span>
          <button
            type="button"
            onClick={() => {
              setSiteKey('apollon');
              if (currentView !== 'home') setCurrentView('home');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className={`px-2.5 py-1 sm:py-0.5 rounded-full font-bold transition-all cursor-pointer text-[10px] sm:text-[11px] flex items-center gap-1 ${
              siteKey === 'apollon'
                ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-slate-950 font-black shadow-md'
                : 'text-slate-300 hover:text-white'
            }`}
            title="Apollon Güzellik & Medikal Estetik Ana Portalını Aç"
          >
            <span>👑</span>
            <span>Apollon Güzellik</span>
          </button>
          <button
            type="button"
            onClick={() => {
              setSiteKey('erkek');
              if (currentView !== 'home') setCurrentView('home');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className={`px-2.5 py-1 sm:py-0.5 rounded-full font-bold transition-all cursor-pointer text-[10px] sm:text-[11px] flex items-center gap-1 ${
              siteKey === 'erkek'
                ? 'bg-amber-400 text-slate-950 font-black shadow-md'
                : 'text-slate-300 hover:text-white'
            }`}
            title="Bursa Erkek Lazer Epilasyon Kliniğini Aç"
          >
            <span>👨</span>
            <span>Erkek Lazer</span>
          </button>
          <button
            type="button"
            onClick={() => {
              setSiteKey('kadin');
              if (currentView !== 'home') setCurrentView('home');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className={`px-2.5 py-1 sm:py-0.5 rounded-full font-bold transition-all cursor-pointer text-[10px] sm:text-[11px] flex items-center gap-1 ${
              siteKey === 'kadin'
                ? 'bg-rose-500 text-white font-black shadow-md'
                : 'text-slate-300 hover:text-white'
            }`}
            title="Bursa Kadın Lazer Epilasyon Kliniğini Aç"
          >
            <span>👩</span>
            <span>Kadın Lazer</span>
          </button>
        </aside>
      )}

    </div>
  );
}

export default function App() {
  return (
    <SiteProvider>
      <AppContent />
    </SiteProvider>
  );
}
